import type { WidgetInstance } from "../apollo-client";
import { fetchSessionContent } from "../targeting/session-content";
import { previewReasonFromBody } from "./preview-codes";

/**
 * Watches one visitor SSE stream for a rejected PREVIEW token (BE 062). The stream itself never
 * carries the preview token: it authorises with the visitor JWT in `?token=`. Two ways it learns
 * the token is dead, both answered by asking the host for a new one:
 *
 *  - the BE closes the stream with an `event: error` frame whose data is `{ code }`;
 *  - the connect was refused (`EventSource` hides the status): the browser gave up, so the
 *    session endpoint is asked once, with the header the stream cannot send, for the real code.
 *
 * Returns the teardown. Inert (and free) when there is no preview token.
 */
export function watchPreviewStream(
  source: EventSource,
  instance: WidgetInstance,
  visitorToken: string,
): () => void {
  let probed = false;
  // The stream belongs to the preview token it was opened under.
  const streamToken = instance.preview.token;

  const onErrorFrame = (event: Event) => {
    if (!instance.preview.active) return;
    if (event instanceof MessageEvent) {
      if (typeof event.data !== "string") return;
      try {
        const reason = previewReasonFromBody(JSON.parse(event.data));
        if (reason) instance.preview.report(reason, streamToken);
      } catch {
        /* not a coded frame */
      }
      return;
    }
    // A plain error event: a refused connect leaves the source CLOSED (no auto-reconnect).
    if (source.readyState !== 2 || probed || !instance.publicKey) return;
    probed = true;
    const usedToken = streamToken;
    void fetchSessionContent({
      publicKey: instance.publicKey,
      token: visitorToken,
      previewToken: usedToken,
    }).then((res) => {
      if (res.kind === "unauthorized" && res.previewReason) {
        instance.preview.report(res.previewReason, usedToken);
      }
    });
  };

  source.addEventListener("error", onErrorFrame);
  return () => source.removeEventListener("error", onErrorFrame);
}
